import React, { useCallback, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { GroupCard } from './GroupCard';
import { generateId } from '../../../utils/helpFunctions';
import CustomModal from '../../../components/CustomModal';
import CustomInput from '../../../components/CustomInput';
import Title from '../../../components/Title';
import { Empty } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
    allFaqsGroups,
    createFaqsGroup,
    deleteFaqsGroup,
    editFaqsGroup,
    faqsGroupsLoadingStatus,
    getFaqsGroups,
    reorderFaqsGroups,
} from '../../../redux/slices/faqSlice';
import { toast } from 'react-toastify';
import LocalLoader from '../../../components/LocalLoader';
import MultinationalTabs from '../../../components/MultinationalTabs';

const initialGroupNameState = {
    sv: {
        name: '',
    },
    no: {
        name: '',
    },
    fi: {
        name: '',
    },
    da: {
        name: '',
    },
    en: {
        name: '',
    },
    is: {
        name: '',
    },
};

export default function GroupsPage() {
    const dispatch = useDispatch();

    const groups = useSelector(allFaqsGroups);
    const groupsLoadingStatus = useSelector(faqsGroupsLoadingStatus);

    const [cards, setCards] = useState([]);
    const [groupName, setGroupName] = useState(initialGroupNameState);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [isAddGroupModalVisible, setIsAddGroupModalVisible] = useState(false);
    const [isEditGroupModalVisible, setIsEditGroupModalVisible] =
        useState(false);
    const [isDeleteGroupModalVisible, setIsDeleteGroupModalVisible] =
        useState(false);
    const [errors, setErrors] = useState({});

    const sortGroupsAsc = (groups) =>
        groups?.toSorted((a, b) => a.position - b.position);

    const createCountriesLocalizationsGroupNames = (data) =>
        data?.reduce((acc, el) => {
            acc[el.lang_code.toLowerCase()] = el;
            return acc;
        }, {});

    useEffect(() => {
        dispatch(getFaqsGroups());
    }, [dispatch]);

    useEffect(() => {
        const sortedGroups = sortGroupsAsc(groups);
        setCards(sortedGroups);
    }, [dispatch, groups]);

    const onUpdateCardsOrder = useCallback(
        async (cards) => {
            const body = { groups: cards };
            try {
                const groups = await dispatch(reorderFaqsGroups(body)).unwrap();
                const sortedGroups = sortGroupsAsc(groups);
                toast.success('Groups order successfully changed');
                setCards(sortedGroups);
            } catch (error) {
                setErrors(error.errors);
            }
        },
        [dispatch],
    );

    const moveCard = useCallback((dragIndex, hoverIndex) => {
        setCards((prevCards) => {
            const cardsCopy = JSON.parse(JSON.stringify(prevCards));

            cardsCopy.splice(dragIndex, 1);
            cardsCopy.splice(hoverIndex, 0, prevCards[dragIndex]);

            const cardsWithPosition = cardsCopy.map((el, i) => {
                return { ...el, position: i };
            });

            return cardsWithPosition;
        });
    }, []);

    const dropCard = useCallback(() => {
        onUpdateCardsOrder(cards);
    }, [cards, onUpdateCardsOrder]);

    const toggleAddGroupModal = () => {
        setGroupName(initialGroupNameState);
        setErrors({});
        setIsAddGroupModalVisible(!isAddGroupModalVisible);
    };

    const toggleEditGroupModal = useCallback(
        (record) => {
            if (!record?.id) {
                setIsEditGroupModalVisible(!isEditGroupModalVisible);
                setSelectedGroup(null);
                setGroupName(initialGroupNameState);
            } else {
                const groupNames = createCountriesLocalizationsGroupNames(
                    record.names,
                );
                setSelectedGroup(record);
                setGroupName(groupNames);
                setErrors({});
                setIsEditGroupModalVisible(!isEditGroupModalVisible);
            }
        },
        [isEditGroupModalVisible],
    );

    const toggleDeleteGroupModal = useCallback(
        (record) => {
            if (!record?.id) {
                setIsDeleteGroupModalVisible(!isDeleteGroupModalVisible);
                setGroupName(initialGroupNameState);
                setSelectedGroup(null);
            } else {
                setSelectedGroup(record);
                setGroupName(record.names);
                setErrors({});
                setIsDeleteGroupModalVisible(!isDeleteGroupModalVisible);
            }
        },
        [isDeleteGroupModalVisible],
    );

    const createRequestGroupNames = useCallback(
        (data) =>
            Object.entries(data).map((el) => ({
                lang_code: el[0].toLowerCase(),
                ...el[1],
            })),
        [],
    );

    const onAddGroup = async () => {
        const newGroup = {
            position: cards.length,
            id: generateId(),
            names: createRequestGroupNames(groupName),
        };

        try {
            await dispatch(createFaqsGroup(newGroup)).unwrap();
            toast.success('New group successfully added');
            dispatch(getFaqsGroups());
            toggleAddGroupModal();
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onEditGroup = async () => {
        try {
            await dispatch(
                editFaqsGroup({
                    id: selectedGroup.id,
                    body: {
                        ...selectedGroup,
                        names: createRequestGroupNames(groupName),
                    },
                }),
            ).unwrap();
            toast.success('Group name successfully updated');
            dispatch(getFaqsGroups());
            toggleEditGroupModal();
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onDeleteGroup = async () => {
        try {
            await dispatch(deleteFaqsGroup(selectedGroup.id)).unwrap();
            toast.success('Group deleted successfully');
            toggleDeleteGroupModal();
            dispatch(getFaqsGroups());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onInputChange = (e, flag) => {
        const { value } = e.target;

        setGroupName((prev) => ({ ...prev, [flag]: { name: value } }));
    };

    const renderCard = useCallback(
        (card, index) => {
            return (
                <GroupCard
                    key={card.id}
                    index={index}
                    card={card}
                    moveCard={moveCard}
                    dropCard={dropCard}
                    onEditCard={toggleEditGroupModal}
                    onDeleteCard={toggleDeleteGroupModal}
                />
            );
        },
        [dropCard, moveCard, toggleDeleteGroupModal, toggleEditGroupModal],
    );

    const foundNames =
        selectedGroup?.names?.find(
            (el) => el.lang_code === 'en' && !!el.name,
        ) || selectedGroup?.names?.find((el) => !!el.name);

    return (
        <div>
            <Title>Add/order groups</Title>
            <div className="details-table-wrapper">
                <div className="table-top-pannel">
                    <div className="table-top-pannel__info">
                        <h2 className="table-top-pannel__title">Prio order</h2>
                    </div>
                    <button
                        className="ant-btn ant-btn-primary ant-btn-lg"
                        onClick={toggleAddGroupModal}
                        type="button"
                    >
                        Add group
                    </button>
                </div>
                <LocalLoader
                    size={30}
                    isLoading={groupsLoadingStatus === 'loading'}
                >
                    {!cards.length && groupsLoadingStatus !== 'loading' ? (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                        <DndProvider backend={HTML5Backend}>
                            <div className="group-cards__list">
                                {cards.map((card, i) => renderCard(card, i))}
                            </div>
                        </DndProvider>
                    )}
                </LocalLoader>
            </div>

            <CustomModal
                title="Add new group"
                subTextAlign="left"
                isModalVisible={isAddGroupModalVisible}
                onAcceptButtonClick={onAddGroup}
                onCancelButtonClick={toggleAddGroupModal}
                cancelButtonText="Cancel"
                acceptButtonText="Add"
                width={500}
            >
                <MultinationalTabs
                    renderWithFlag={(flag) => (
                        <CustomInput
                            id={`name_${flag}`}
                            placeholder="Add group name"
                            onInputChange={(e) => onInputChange(e, flag)}
                            inputValue={groupName[flag].name}
                            labelText="Group name"
                            width="100%"
                            errorText={errors?.names}
                        />
                    )}
                />
            </CustomModal>

            <CustomModal
                title="Edit group name"
                subTextAlign="left"
                isModalVisible={isEditGroupModalVisible}
                onAcceptButtonClick={onEditGroup}
                onCancelButtonClick={toggleEditGroupModal}
                cancelButtonText="Cancel"
                acceptButtonText="Save"
                width={500}
            >
                <MultinationalTabs
                    renderWithFlag={(flag) => (
                        <CustomInput
                            id={`name_${flag}`}
                            placeholder="Add group name"
                            onInputChange={(e) => onInputChange(e, flag)}
                            inputValue={groupName[flag].name}
                            labelText="Group name"
                            width="100%"
                            errorText={errors?.names}
                        />
                    )}
                />
            </CustomModal>

            <CustomModal
                title={
                    <p>
                        Are you sure you want to delete
                        <span className="highlighted-text">{` ${foundNames?.name} `}</span>
                        group?
                    </p>
                }
                subText="You will delete this group without recovery"
                subTextAlign="center"
                isModalVisible={isDeleteGroupModalVisible}
                onAcceptButtonClick={onDeleteGroup}
                onCancelButtonClick={toggleDeleteGroupModal}
                cancelButtonText="Cancel"
                acceptButtonText="Delete"
            ></CustomModal>
        </div>
    );
}
